import { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface ActivityLogViewProps {
  tenantId: string;
  appId: string;
}

export function ActivityLogView({ tenantId, appId }: ActivityLogViewProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !appId) return;
    
    setLoading(true);
    const logsRef = collection(db, `portalTenants/${tenantId}/apps/${appId}/activityLogs`);
    const q = query(logsRef, orderBy('createdAtMs', 'desc'), limit(100));

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const nextLogs = snap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLogs(nextLogs);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to fetch activity logs:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId, appId]);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-[#15803d]/10 text-[#15803d] border-[#15803d]/20';
      case 'failed':
      case 'error':
        return 'bg-error-container text-on-error-container border-error/20';
      case 'ignored':
        return 'bg-surface-container-high text-on-surface-variant border-outline-variant';
      default:
        return 'bg-surface-container-high text-on-surface-variant border-outline-variant';
    }
  };

  return (
    <div className="flex flex-col gap-lg max-w-5xl">
      {error && (
        <div className="p-md bg-error-container text-on-error-container border border-error/20 rounded-lg font-body-sm shadow-sm">
          {error}
        </div>
      )}

      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm overflow-hidden">
        <div className="p-lg border-b border-outline-variant bg-surface-container-low/30">
          <h2 className="font-headline-md text-[20px] font-semibold text-on-surface">Activity Logs</h2>
          <p className="font-body-sm text-[13px] text-on-surface-variant mt-1">
            Realtime stream of system events, webhooks, and verification logs for this app.
          </p>
        </div>

        {loading ? (
          <div className="p-xl flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-xl text-center text-on-surface-variant font-body-sm flex flex-col items-center">
            <span className="material-symbols-outlined text-[48px] mb-2 opacity-50">history</span>
            <p>No activity logs found for this application yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-outline-variant/50">
            {logs.map((log) => (
              <div key={log.id} className="p-md hover:bg-surface-container-lowest/50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-sm mb-1">
                      <span className={`px-2 py-0.5 text-[11px] font-bold uppercase rounded-md border ${getStatusStyle(log.status)}`}>
                        {log.status}
                      </span>
                      <span className="font-mono text-[12px] font-semibold text-primary/80 uppercase tracking-wide">
                        {log.type}
                      </span>
                    </div>
                    <p className="font-body-md text-[14px] text-on-surface mt-2 font-medium break-words">
                      {log.message}
                    </p>
                    
                    {log.payload && Object.keys(log.payload).length > 0 && (
                      <pre className="mt-sm p-3 bg-inverse-surface text-surface rounded-lg font-mono text-[11px] overflow-auto max-h-[200px]">
                        {JSON.stringify(log.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 text-right sm:ml-4">
                    <div className="font-mono text-[12px] text-on-surface-variant">
                      {new Date(log.createdAtMs).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
